/**
 * Installer Placio comme une application (retour #19). Le manifeste
 * (public/manifest.webmanifest) suffit à Chrome et Edge, qui proposent
 * l'installation par l'événement `beforeinstallprompt` : on le garde pour
 * l'entrée « Installer l'application » du menu de l'avatar. Safari (Mac,
 * iPhone, iPad) installe depuis son propre menu : on explique où. Pas de
 * service worker : rien n'est mis en cache, l'application reste toujours à jour.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;

/** À appeler une fois au démarrage (main.tsx) : l'événement arrive tôt. */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // pas de bandeau du navigateur : on propose depuis le menu
    deferred = e as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => { deferred = null; });
}

/** Déjà ouverte comme application (fenêtre à part, écran d'accueil). */
export function isInstalledApp(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Lance l'installation (Chrome, Edge) ; sinon renvoie l'explication à afficher
 * pour ce navigateur. `null` = la fenêtre d'installation du navigateur s'est ouverte.
 */
export async function installApp(): Promise<string | null> {
  if (deferred) {
    const e = deferred;
    deferred = null;
    await e.prompt();
    return null;
  }
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'Sur iPhone ou iPad : dans Safari, touche le bouton Partager puis « Sur l’écran d’accueil ».';
  }
  if (/Firefox\//.test(ua)) {
    return 'Firefox ne sait pas installer d’application. Ouvre Placio dans Chrome, Edge ou Safari pour l’installer.';
  }
  if (/Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\//.test(ua)) {
    return 'Dans Safari : menu Fichier puis « Ajouter au Dock ».';
  }
  return 'Dans la barre d’adresse, clique sur l’icône d’installation (un écran avec une flèche), ou dans le menu du navigateur sur « Installer Placio ».';
}
